// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Tooltip } from '@mui/material';
import { useConst } from '@shared/ui-kit';
import { type FC, useCallback, useMemo } from 'react';

export interface ValueCreationProgressProps {
  readonly value: number;
  readonly max: number;
  readonly min: number;
}

export const ValueCreationProgress: FC<ValueCreationProgressProps> = ({
  value,
  max,
  min,
}) => {
  const isPositive = useMemo(() => value > 0, [value]);

  const LABEL_PADDING_SIZE = useConst(8);

  const MIN_VALUE = useMemo(
    () => (value === 0 ? 0 : LABEL_PADDING_SIZE),
    [LABEL_PADDING_SIZE, value],
  );

  const MAX_VALUE = useMemo(
    () => Math.abs(isPositive ? max : min),
    [isPositive, max, min],
  );

  const normalize = useCallback(
    (value: number, max = 100) =>
      +(((value - MIN_VALUE) / (max - MIN_VALUE)) * 100).toFixed(),
    [MIN_VALUE],
  );

  const normalizedValue = useMemo(
    () => normalize(value, MAX_VALUE),
    [MAX_VALUE, normalize, value],
  );

  const formattedValue = useMemo(
    () =>
      value.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        signDisplay: 'exceptZero',
      }),
    [value],
  );

  const valueAbs = useMemo(() => Math.abs(normalizedValue), [normalizedValue]);

  return (
    <Tooltip title={formattedValue} data-testid="tooltip">
      <div className="value-creation">
        <progress
          value={valueAbs}
          max={100}
          className="value-creation-progress"
          data-testid="progress-bar-negative"
          dir="rtl"
          aria-hidden={isPositive || valueAbs === 0}
        />
        <hr className="flex h-[24px] border border-border-sep" />
        <progress
          value={valueAbs}
          max={100}
          className="value-creation-progress"
          data-testid="progress-bar-positive"
          dir="lrt"
          aria-hidden={!isPositive || valueAbs === 0}
        />
      </div>
    </Tooltip>
  );
};
